import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getIntrusionAccounts,
  getKivoBranches,
  getCompanyPickerConfig,
  updatePersonDetails,
  updatePersonIntrusionProfile,
  updatePersonVisitorProfile,
  updatePersonVendorProfile,
} from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

const DESTINATION_BY_SOURCE = {
  employee: ['vendor', 'visitor', 'intrusion'],
  vendor: ['employee', 'visitor', 'intrusion'],
  visitor: ['employee', 'vendor', 'intrusion'],
  intrusion: ['employee', 'vendor', 'visitor'],
};

const TARGET_LABELS = {
  employee: 'Employee',
  vendor: 'Vendor',
  visitor: 'Visitor',
  intrusion: 'Intrusion Profile',
};

const PROFILE_KIND_DISPLAY = {
  employee: 'Employee',
  vendor: 'Vendor',
  visitor: 'Visitor',
  intrusion: 'Intrusion Profile',
};

export function buildProfileSwitchSuccessMessage(sourceKind, targetKind) {
  const from = PROFILE_KIND_DISPLAY[sourceKind] || 'Profile';
  const to = PROFILE_KIND_DISPLAY[targetKind] || 'Profile';
  return `${from} switched to ${to} successfully`;
}

const companiesFromKivoConfigData = (data) => {
  const configs = Array.isArray(data?.configs) ? data.configs : [];
  const seen = new Set();
  const companies = [];
  configs.forEach((cfg) => {
    const companyId = cfg.company_id == null ? '' : String(cfg.company_id);
    const companyName = cfg.company_name || 'Default Company';
    if (!companyId || seen.has(companyId)) return;
    seen.add(companyId);
    companies.push({ company_id: companyId, company_name: companyName });
  });
  return companies;
};

const PersonProfileSwitchDialog = ({
  open,
  sourceKind,
  person,
  onClose,
  onSuccess,
  onError,
  companyOptions,
}) => {
  const { confirm } = useConfirmDialog();
  const { permissions, isIntrusion } = useAuth();
  const vendorManagementEnabled = permissions?.feature_flags?.vendor_management === true;
  const visitorManagementEnabled = permissions?.feature_flags?.visitor_management === true;
  const intrusionProfileEnabled = isIntrusion === true;

  const destinations = useMemo(
    () => {
      const raw = DESTINATION_BY_SOURCE[sourceKind] || [];
      return raw.filter((kind) => {
        if (kind === 'vendor' && !vendorManagementEnabled) return false;
        if (kind === 'visitor' && !visitorManagementEnabled) return false;
        if (kind === 'intrusion' && !intrusionProfileEnabled) return false;
        return true;
      });
    },
    [sourceKind, vendorManagementEnabled, visitorManagementEnabled, intrusionProfileEnabled],
  );
  const [targetKind, setTargetKind] = useState('');
  const [intrusionAccounts, setIntrusionAccounts] = useState(undefined);
  const [intrusionCompanyId, setIntrusionCompanyId] = useState('');
  const [employeeCompanies, setEmployeeCompanies] = useState(undefined);
  const [branches, setBranches] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgSelectMenuWidth, setOrgSelectMenuWidth] = useState(null);
  const isEmployeeTarget = targetKind === 'employee';
  const [comment, setComment] = useState(person?.comment?.content || '');

  const resetForm = useCallback(() => {
    const raw = DESTINATION_BY_SOURCE[sourceKind] || [];
    const allowed = raw.filter((kind) => {
      if (kind === 'vendor' && !vendorManagementEnabled) return false;
      if (kind === 'visitor' && !visitorManagementEnabled) return false;
      if (kind === 'intrusion' && !intrusionProfileEnabled) return false;
      return true;
    });
    setTargetKind(allowed[0] || '');
    setIntrusionAccounts(undefined);
    setIntrusionCompanyId('');
    setEmployeeCompanies(undefined);
    setBranches([]);
    setCompanyId('');
    setBranchId('');
    setLoading(false);
    setSaving(false);

  }, [sourceKind, vendorManagementEnabled, visitorManagementEnabled, intrusionProfileEnabled]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setComment(person?.comment?.content || '');
  }, [open, person?.person_id, person?.comment?.content, resetForm]);

  useEffect(() => {
    if (!open || !targetKind) return;
    let cancelled = false;

    const loadTargetData = async () => {
      if (targetKind === 'intrusion' && intrusionAccounts === undefined) {
        setLoading(true);
        try {
          const res = await getIntrusionAccounts(100, 0, true);
          if (cancelled) return;
          const accounts = res.companies || [];
          setIntrusionAccounts(accounts);
          setIntrusionCompanyId(accounts.length === 1 ? String(accounts[0].company_id || '') : '');
        } catch (e) {
          if (!cancelled) {
            onError?.(e?.message || 'Failed to load intrusion accounts');
            setIntrusionAccounts(undefined);
            setIntrusionCompanyId('');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (targetKind === 'employee' || targetKind === 'vendor' || targetKind === 'visitor') {
        setLoading(true);
        try {

          if(employeeCompanies===undefined && companyOptions===undefined){
            
            const res = await getCompanyPickerConfig(permissions);
            setEmployeeCompanies(companiesFromKivoConfigData(res))
          }
          
          if(Array.isArray(companyOptions)){
            setEmployeeCompanies(companyOptions);
          }

          if (cancelled) return;
          setCompanyId('');
          setBranchId('');
          setBranches([]);
        } catch (e) {
          if (!cancelled) {
            onError?.(e?.message || 'Could not load companies from Kivo');
            setEmployeeCompanies(undefined);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    };

    loadTargetData();
    return () => {
      cancelled = true;
    };
  }, [open, targetKind, onError, permissions]);

  const onCompanyChange = async (nextCompanyId) => {
    const normalizedCompanyId = nextCompanyId == null ? '' : String(nextCompanyId);
    setCompanyId(normalizedCompanyId);
    setBranchId('');
    setBranches([]);
    if (!normalizedCompanyId) return;
    try {
      const br = await getKivoBranches(normalizedCompanyId);
      const branchRows = Array.isArray(br?.branches)
        ? br.branches.map((b) => ({
            branch_id: b.branch_id == null ? '' : String(b.branch_id),
            branch_name: b.branch_name || b.branch_address || (b.branch_id == null ? '' : String(b.branch_id)),
          }))
        : [];
      setBranches(branchRows);
    } catch (e) {
      onError?.(e?.message || 'Failed to load branches');
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleConfirm = async () => {
    if (!person?.person_id || !targetKind) return;

    if (targetKind === 'visitor' && !comment.trim()) {
      onError?.('Comment is required when switching to Visitor', 'warning');
      return;
    }

    if (targetKind === 'intrusion' && !intrusionCompanyId) {
      onError?.('Select an intrusion account', 'warning');
      return;
    }
    if (isEmployeeTarget && !companyId) {
      onError?.('Select a company for the employee profile', 'warning');
      return;
    }
    if (isEmployeeTarget && !branchId) {
      onError?.('Select a branch for the employee profile', 'warning');
      return;
    }
    if ((targetKind === 'employee' || targetKind === 'vendor' || targetKind === 'visitor') && branchId && !companyId) {
      onError?.('Select a company if you choose a branch', 'warning');
      return;
    }

    const approved = await confirm({
      title: 'Confirm Switch Profile',
      message: 'Are you sure you want to switch this profile?',
      confirmLabel: 'Confirm',
      danger: false,
    });
    if (!approved) return;

    setSaving(true);
    try {
      const personId = person.person_id;

      if (targetKind === 'intrusion') {
        await updatePersonIntrusionProfile(personId, {
          is_intrusion: true,
          company_id: intrusionCompanyId,
        });
      } else if (targetKind === 'employee') {
        if (sourceKind === 'vendor') {
          await updatePersonVendorProfile(personId, {
            is_vendor: false,
            ...(companyId ? { company_id: companyId, branch_id: branchId || null } : {}),
          });
        } else if (sourceKind === 'visitor') {
          await updatePersonVisitorProfile(personId, {
            is_visitor: false,
            ...(companyId ? { company_id: companyId, branch_id: branchId || null } : {}),
          });
          if (companyId) {
            await updatePersonDetails(personId, personId, companyId, branchId || null);
          }
        } else {
          await updatePersonIntrusionProfile(personId, {
            is_intrusion: false,
            ...(companyId ? { company_id: companyId, branch_id: branchId || null } : {}),
          });
        }
      } else if (targetKind === 'vendor') {
        if (sourceKind === 'intrusion') {
          await updatePersonIntrusionProfile(personId, { is_intrusion: false });
        } else if (sourceKind === 'visitor') {
          await updatePersonVisitorProfile(personId, {
            is_visitor: false,
            ...(companyId ? { company_id: companyId, branch_id: branchId || null } : {}),
          });
        }
        await updatePersonVendorProfile(personId, {
          is_vendor: true,
          ...(companyId ? { company_id: companyId, branch_id: branchId || null } : {}),
        });
      } else if (targetKind === 'visitor') {
        if (sourceKind === 'intrusion') {
          await updatePersonIntrusionProfile(personId, { is_intrusion: false });
        } else if (sourceKind === 'vendor') {
          await updatePersonVendorProfile(personId, { is_vendor: false });
        }
        await updatePersonVisitorProfile(personId, { is_visitor: true, comment_content: comment.trim()  });
        if (companyId) {
          await updatePersonDetails(personId, personId, companyId, branchId || null);
        }
      }

      onSuccess?.(buildProfileSwitchSuccessMessage(sourceKind, targetKind));
      onClose?.();
    } catch (e) {
      onError?.(e?.message || 'Failed to switch profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDisabled =
    saving
    || loading
    || !targetKind
    || (targetKind === 'intrusion' && (!intrusionCompanyId || intrusionAccounts.length === 0))
    || (isEmployeeTarget && (!companyId || !branchId || employeeCompanies?.length === 0))
    || (targetKind === 'visitor' && !comment.trim());

  const selectedCompanyName = useMemo(() => {
    if (!companyId) return '';
    const match = employeeCompanies?.find(
      (company) => String(company.company_id) === String(companyId),
    );
    return match?.company_name || '';
  }, [companyId, employeeCompanies]);

  const selectedBranchName = useMemo(() => {
    if (!branchId) return '';
    const match = branches.find(
      (branch) => String(branch.branch_id) === String(branchId),
    );
    return match?.branch_name || '';
  }, [branchId, branches]);

  const renderSelectValueWithTooltip = useCallback((label, { disableTooltip = false } = {}) => {
    const safeLabel = String(label || '');
    const shouldDisable = disableTooltip || !safeLabel;
    return (
      <Tooltip
        title={safeLabel}
        arrow
        placement="top"
        disableHoverListener={shouldDisable}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 520,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {safeLabel}
        </Box>
      </Tooltip>
    );
  }, []);

  const renderMenuItemLabel = useCallback((label) => {
    const safeLabel = String(label || '');
    return (
      <Tooltip
        title={safeLabel}
        arrow
        placement="top"
        disableHoverListener={!safeLabel}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 520,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {safeLabel}
        </Box>
      </Tooltip>
    );
  }, []);

  const handleOrgSelectOpen = useCallback((event) => {
    const control = event.currentTarget.closest('.MuiFormControl-root');
    const width = control?.getBoundingClientRect().width
      ?? event.currentTarget.getBoundingClientRect().width;
    setOrgSelectMenuWidth(width > 0 ? width : null);
  }, []);

  const orgSelectMenuProps = useMemo(() => ({
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      style: orgSelectMenuWidth
        ? { width: orgSelectMenuWidth, maxWidth: orgSelectMenuWidth }
        : undefined,
      sx: {
        mt: 1,
        boxSizing: 'border-box',
        overflow: 'hidden',
      },
    },
    MenuListProps: {
      sx: {
        width: '100%',
        maxWidth: '100%',
        py: 0,
      },
    },
  }), [orgSelectMenuWidth]);

  const orgSelectFieldSx = {
    minWidth: 0,
    '& .MuiSelect-select': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const targetDescription = useMemo(() => {
    if (targetKind === 'intrusion') {
      return 'All active face angles use the intrusion account below. Branch is cleared to match the new account.';
    }
    if (targetKind === 'employee') {
      if (sourceKind === 'vendor') {
        return 'This person will appear under Employees. Vendor role is cleared from face records. Select the company and branch this employee belongs to.';
      }
      if (sourceKind === 'visitor') {
        return 'This person will appear under Employees. Visitor role is cleared from face records. Select the company and branch this employee belongs to.';
      }
      return 'This person will appear under Employees. Select the company and branch this employee belongs to.';
    }
    if (targetKind === 'vendor') {
      return 'This person will appear under Vendors. Optionally select a company and branch to assign now.';
    }
    if (targetKind === 'visitor') {
      return 'This person will appear under Visitors. Optionally select a company and branch to assign now.';
    }
    return '';
  }, [sourceKind, targetKind]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Switch profile</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {person?.name} ({person?.person_id})
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Switch to
          </Typography>
          <RadioGroup
            value={targetKind}
            onChange={(e) => {
              const next = e.target.value;
              setTargetKind(next);
              if (next !== 'employee') {
                setCompanyId('');
                setBranchId('');
                setBranches([]);
              }
            }}
          >
            {destinations.map((kind) => (
              <FormControlLabel
                key={kind}
                value={kind}
                control={<Radio size="small" />}
                label={TARGET_LABELS[kind]}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {targetDescription ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {targetDescription}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!loading && targetKind === 'intrusion' ? (
          intrusionAccounts?.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No intrusion accounts found. Create one under Settings → Accounts first.
            </Typography>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="profile-switch-intrusion-account-label">Intrusion account</InputLabel>
              <Select
                labelId="profile-switch-intrusion-account-label"
                label="Intrusion account"
                value={intrusionCompanyId}
                onChange={(e) => setIntrusionCompanyId(e.target.value)}
              >
                {intrusionAccounts?.map((a) => (
                  <MenuItem key={a.company_id} value={a.company_id}>
                    {a.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        ) : null}

        {!loading && (targetKind === 'employee' || targetKind === 'vendor' || targetKind === 'visitor') ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {employeeCompanies?.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {targetKind === 'employee'
                  ? 'No companies in Kivo config. Add company and branch configuration before switching this person to Employees.'
                  : targetKind === 'vendor'
                  ? (sourceKind === 'intrusion'
                    ? 'No companies in Kivo config. Confirm still moves this person to Vendors and clears the intrusion account from their face records.'
                    : sourceKind === 'visitor'
                    ? 'No companies in Kivo config. Confirm still moves this person to Vendors and clears the visitor role from their face records.'
                    : 'No companies in Kivo config. Confirm still moves this person to Vendors.')
                  : (sourceKind === 'intrusion'
                    ? 'No companies in Kivo config. Confirm still moves this person to Visitors and clears the intrusion account from their face records.'
                    : sourceKind === 'vendor'
                    ? 'No companies in Kivo config. Confirm still moves this person to Visitors and clears the vendor role from their face records.'
                    : 'No companies in Kivo config. Confirm still moves this person to Visitors.')}
              </Typography>
            ) : (
              <>
                <FormControl fullWidth required={isEmployeeTarget} sx={orgSelectFieldSx}>
                  <InputLabel id="profile-switch-org-company-label">
                    {isEmployeeTarget ? 'Company' : 'Company (optional)'}
                  </InputLabel>
                  <Select
                    labelId="profile-switch-org-company-label"
                    label={isEmployeeTarget ? 'Company' : 'Company (optional)'}
                    value={companyId}
                    renderValue={(value) => renderSelectValueWithTooltip(
                      value ? selectedCompanyName : '',
                      { disableTooltip: !value },
                    )}
                    MenuProps={orgSelectMenuProps}
                    onOpen={handleOrgSelectOpen}
                    onChange={(e) => onCompanyChange(e.target.value)}
                  >
                    {employeeCompanies?.map((c) => (
                      <MenuItem
                        key={c.company_id}
                        value={c.company_id}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                      >
                        {renderMenuItemLabel(c.company_name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required={isEmployeeTarget} disabled={!companyId} sx={orgSelectFieldSx}>
                  <InputLabel id="profile-switch-org-branch-label">
                    {isEmployeeTarget ? 'Branch' : 'Branch (optional)'}
                  </InputLabel>
                  <Select
                    labelId="profile-switch-org-branch-label"
                    label={isEmployeeTarget ? 'Branch' : 'Branch (optional)'}
                    value={branchId}
                    renderValue={(value) => renderSelectValueWithTooltip(
                      value ? selectedBranchName : '',
                      { disableTooltip: !value },
                    )}
                    MenuProps={orgSelectMenuProps}
                    onOpen={handleOrgSelectOpen}
                    onChange={(e) => setBranchId(e.target.value)}
                  >
                    {branches.map((b) => (
                      <MenuItem
                        key={b.branch_id}
                        value={b.branch_id}
                        sx={{ minWidth: 0, maxWidth: '100%' }}
                      >
                        {renderMenuItemLabel(b.branch_name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Stack>
        ) : null}

        {!loading && targetKind === 'visitor' ? (
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Comment"
            value={comment}
            onChange={(e) => {
              if (e.target.value.length <= 200) {
                setComment(e.target.value);
              }
            }}
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 200 }}
            helperText={`${comment.length}/200 characters`}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={confirmDisabled}>
          {saving ? <CircularProgress size={22} /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PersonProfileSwitchDialog;
